<script setup>
import { computed } from 'vue';
import { Check, Clock, X, LoaderCircle, Circle, CircleAlert, Undo2 } from 'lucide-vue-next';
import { STATUS } from '../data/status.js';

const props = defineProps({
  /** Status key from data/status.js, for example settled, pending, failed, verified */
  status: { type: String, default: null },
  /** Used when no status is given: neutral, success, warning, danger, info, brand, ink */
  tone: { type: String, default: 'neutral' },
  /** sm or md */
  size: { type: String, default: 'sm' },
  /** Hides the leading icon */
  plain: { type: Boolean, default: false },
  /** Replaces the default status label, for example "Verified recipient" */
  label: { type: String, default: '' },
});

const iconMap = { check: Check, clock: Clock, x: X, loader: LoaderCircle, circle: Circle, alert: CircleAlert, undo: Undo2 };
const def = computed(() => (props.status ? STATUS[props.status] : null));
const resolvedTone = computed(() => def.value?.tone || props.tone);
const icon = computed(() => (props.plain || !def.value ? null : iconMap[def.value.icon]));
</script>

<template>
  <span class="pd-badge" :class="[`pd-badge--${resolvedTone}`, `pd-badge--${size}`]">
    <component :is="icon" v-if="icon" :size="size === 'md' ? 14 : 12" :stroke-width="2.75" aria-hidden="true" />
    <slot>{{ label || def?.label }}</slot>
  </span>
</template>

<style scoped>
.pd-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 24px;
  padding: 0 10px;
  border-radius: var(--pd-r-pill);
  font-size: 0.75rem;
  font-weight: 800;
  white-space: nowrap;
  line-height: 1;
}
.pd-badge--md { height: 30px; padding: 0 14px; font-size: 0.8125rem; gap: 6px; }
.pd-badge--neutral { background: var(--pd-sand); color: var(--pd-muted); }
.pd-badge--success { background: var(--pd-success-bg); color: var(--pd-success); }
.pd-badge--warning { background: var(--pd-warning-bg); color: var(--pd-warning); }
.pd-badge--danger { background: var(--pd-danger-bg); color: var(--pd-danger); }
.pd-badge--info { background: var(--pd-info-bg); color: var(--pd-info); }
.pd-badge--brand { background: var(--pd-tint); color: var(--pd-orange-text); }
.pd-badge--ink { background: var(--pd-ink); color: var(--pd-white); }
.pd-badge--orange { background: var(--pd-orange); color: var(--pd-on-orange); }
</style>
